import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, TrendingUp, Target, Shield } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Falcon Admin — CRUD for the 3 collections that drive Falcon View TV:
 *   • Stocks (portfolio_v2)
 *   • Plays  (options_plays)
 *   • IRA    (ira_holdings)
 * Any change here → TV auto-picks it up on the next poll (≤ 30s).
 */

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    className={`rounded-xl border px-4 py-2 text-sm font-black uppercase tracking-widest transition ${
      active ? "border-amber-500 bg-amber-500/20 text-amber-300"
             : "border-slate-800 bg-slate-950 text-slate-500 hover:text-white"
    }`}
    data-testid={testid}
  >
    {children}
  </button>
);

function StocksTab({ rows, reload }) {
  const [form, setForm] = useState({ symbol: "", shares: "", total_return: "0", asset_class: "STOCK" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/falcon-view/admin/stock", { ...form, shares: parseFloat(form.shares), total_return: parseFloat(form.total_return || 0) });
      toast.success(`${form.symbol.toUpperCase()} saved`);
      setForm({ symbol: "", shares: "", total_return: "0", asset_class: "STOCK" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
  };
  const remove = async (sym) => {
    if (!window.confirm(`Delete ${sym}?`)) return;
    await api.delete(`/falcon-view/admin/stock/${sym}`);
    toast.success(`${sym} removed`); reload();
  };
  return (
    <div>
      <form onSubmit={submit} className="mb-4 grid grid-cols-5 gap-2" data-testid="admin-stock-form">
        <input required placeholder="SYMBOL" value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 font-mono uppercase text-white" data-testid="admin-stock-symbol" />
        <input required type="number" step="any" placeholder="Shares" value={form.shares} onChange={(e) => setForm({ ...form, shares: e.target.value })} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-white" data-testid="admin-stock-shares" />
        <input type="number" step="any" placeholder="Total Return $" value={form.total_return} onChange={(e) => setForm({ ...form, total_return: e.target.value })} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-white" />
        <select value={form.asset_class} onChange={(e) => setForm({ ...form, asset_class: e.target.value })} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-white">
          <option value="STOCK">Stock</option>
          <option value="CRYPTO">Crypto</option>
        </select>
        <button className="flex items-center justify-center gap-2 rounded-lg bg-emerald-500/20 px-3 font-black uppercase tracking-widest text-emerald-300 hover:bg-emerald-500/30" data-testid="admin-stock-submit"><Plus className="h-4 w-4" /> Save</button>
      </form>
      <div className="overflow-x-auto rounded-xl border border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/60 text-[10px] uppercase tracking-widest text-slate-500">
            <tr><th className="p-2 text-left">Symbol</th><th className="p-2 text-right">Shares</th><th className="p-2 text-right">Total Return</th><th className="p-2 text-left">Class</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.symbol} className="border-t border-slate-800 hover:bg-slate-900/40" data-testid={`admin-stock-row-${r.symbol}`}>
                <td className="p-2 font-mono font-black text-white">{r.symbol}</td>
                <td className="p-2 text-right text-slate-300">{r.shares?.toFixed(4)}</td>
                <td className={`p-2 text-right font-mono ${(r.total_return || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>${(r.total_return || 0).toFixed(2)}</td>
                <td className="p-2 text-slate-400">{r.asset_class || "STOCK"}</td>
                <td className="p-2 text-right"><button onClick={() => remove(r.symbol)} className="rounded p-1.5 text-red-400 hover:bg-red-500/20" data-testid={`admin-stock-delete-${r.symbol}`}><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PlaysTab({ rows, reload }) {
  const [form, setForm] = useState({ symbol: "", strike: "", contract_type: "CALL", expiry: "", contracts: 1, total_return: "0", is_itm: false });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/falcon-view/admin/play", { ...form, strike: parseFloat(form.strike), contracts: parseInt(form.contracts), total_return: parseFloat(form.total_return || 0) });
      toast.success(`${form.symbol.toUpperCase()} $${form.strike} ${form.contract_type} saved`);
      setForm({ symbol: "", strike: "", contract_type: "CALL", expiry: "", contracts: 1, total_return: "0", is_itm: false });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.symbol} $${r.strike} ${r.contract_type} ${r.expiry}?`)) return;
    await api.delete(`/falcon-view/admin/play?symbol=${r.symbol}&strike=${r.strike}&expiry=${r.expiry}&contract_type=${r.contract_type}`);
    toast.success("Play removed"); reload();
  };
  return (
    <div>
      <form onSubmit={submit} className="mb-4 grid grid-cols-7 gap-2" data-testid="admin-play-form">
        <input required placeholder="SYMBOL" value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 font-mono uppercase text-white" data-testid="admin-play-symbol" />
        <input required type="number" step="0.01" placeholder="Strike" value={form.strike} onChange={(e) => setForm({ ...form, strike: e.target.value })} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-white" />
        <select value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-white">
          <option>CALL</option><option>PUT</option>
        </select>
        <input required type="date" value={form.expiry} onChange={(e) => setForm({ ...form, expiry: e.target.value })} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-white" data-testid="admin-play-expiry" />
        <input required type="number" placeholder="Contracts" value={form.contracts} onChange={(e) => setForm({ ...form, contracts: e.target.value })} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-white" />
        <input type="number" step="any" placeholder="Total Return $" value={form.total_return} onChange={(e) => setForm({ ...form, total_return: e.target.value })} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-white" />
        <button className="flex items-center justify-center gap-2 rounded-lg bg-emerald-500/20 px-3 font-black uppercase tracking-widest text-emerald-300 hover:bg-emerald-500/30" data-testid="admin-play-submit"><Plus className="h-4 w-4" /> Save</button>
      </form>
      <div className="overflow-x-auto rounded-xl border border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/60 text-[10px] uppercase tracking-widest text-slate-500">
            <tr><th className="p-2 text-left">Symbol</th><th className="p-2 text-right">Strike</th><th className="p-2">Type</th><th className="p-2">Expiry</th><th className="p-2 text-right">Contracts</th><th className="p-2 text-right">Return</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r._id_key} className="border-t border-slate-800 hover:bg-slate-900/40" data-testid={`admin-play-row-${r.symbol}-${r.strike}`}>
                <td className="p-2 font-mono font-black text-white">{r.symbol}</td>
                <td className="p-2 text-right text-slate-300">${r.strike}</td>
                <td className={`p-2 text-center font-black ${r.contract_type === "CALL" ? "text-emerald-300" : "text-red-300"}`}>{r.contract_type}</td>
                <td className="p-2 text-slate-400">{r.expiry}</td>
                <td className="p-2 text-right text-slate-300">{r.contracts}</td>
                <td className={`p-2 text-right font-mono ${(r.total_return || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>${(r.total_return || 0).toFixed(2)}</td>
                <td className="p-2 text-right"><button onClick={() => remove(r)} className="rounded p-1.5 text-red-400 hover:bg-red-500/20"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IRATab({ rows, reload }) {
  const [form, setForm] = useState({ symbol: "", shares: "", total_return: "0", company_name: "", account: "Robinhood Roth IRA" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/falcon-view/admin/ira", { symbol: form.symbol, shares: parseFloat(form.shares), total_return: parseFloat(form.total_return || 0), company_name: form.company_name || null, accounts: [form.account] });
      toast.success(`${form.symbol.toUpperCase()} saved to IRA`);
      setForm({ symbol: "", shares: "", total_return: "0", company_name: "", account: "Robinhood Roth IRA" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
  };
  const remove = async (sym) => {
    if (!window.confirm(`Remove ${sym} from IRA?`)) return;
    await api.delete(`/falcon-view/admin/ira/${sym}`);
    toast.success(`${sym} removed`); reload();
  };
  return (
    <div>
      <form onSubmit={submit} className="mb-4 grid grid-cols-6 gap-2" data-testid="admin-ira-form">
        <input required placeholder="SYMBOL" value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 font-mono uppercase text-white" data-testid="admin-ira-symbol" />
        <input required type="number" step="any" placeholder="Shares" value={form.shares} onChange={(e) => setForm({ ...form, shares: e.target.value })} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-white" />
        <input type="number" step="any" placeholder="Total Return $" value={form.total_return} onChange={(e) => setForm({ ...form, total_return: e.target.value })} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-white" />
        <input placeholder="Company (optional)" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-white" />
        <select value={form.account} onChange={(e) => setForm({ ...form, account: e.target.value })} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-white">
          <option>Robinhood Roth IRA</option>
          <option>Fidelity Roth Contributory IRA</option>
        </select>
        <button className="flex items-center justify-center gap-2 rounded-lg bg-emerald-500/20 px-3 font-black uppercase tracking-widest text-emerald-300 hover:bg-emerald-500/30" data-testid="admin-ira-submit"><Plus className="h-4 w-4" /> Save</button>
      </form>
      <div className="overflow-x-auto rounded-xl border border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/60 text-[10px] uppercase tracking-widest text-slate-500">
            <tr><th className="p-2 text-left">Symbol</th><th className="p-2 text-left">Company</th><th className="p-2 text-right">Shares</th><th className="p-2 text-right">Total Return</th><th className="p-2 text-left">Accounts</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.symbol} className="border-t border-slate-800 hover:bg-slate-900/40" data-testid={`admin-ira-row-${r.symbol}`}>
                <td className="p-2 font-mono font-black text-white">{r.symbol}</td>
                <td className="p-2 text-slate-400">{r.company_name || "—"}</td>
                <td className="p-2 text-right text-slate-300">{r.shares?.toFixed(4)}</td>
                <td className={`p-2 text-right font-mono ${(r.total_return || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>${(r.total_return || 0).toFixed(2)}</td>
                <td className="p-2 text-[10px] text-slate-500">{(r.accounts || []).join(" · ") || "—"}</td>
                <td className="p-2 text-right"><button onClick={() => remove(r.symbol)} className="rounded p-1.5 text-red-400 hover:bg-red-500/20"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function FalconAdmin() {
  const [tab, setTab] = useState("stocks");
  const [holdings, setHoldings] = useState({ stocks: [], plays: [], ira: [] });
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/falcon-view/admin/holdings");
      setHoldings(r.data);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Admin only.");
      else toast.error("Failed to load holdings.");
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  return (
    <div className="min-h-screen bg-[#040711] p-6 text-white" data-testid="falcon-admin-root">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex items-center gap-3">
          <button onClick={() => nav("/")} className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs uppercase tracking-widest text-slate-400 hover:text-white" data-testid="admin-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div>
            <h1 className="text-3xl font-black italic tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-orange-400 to-red-500">FALCON ADMIN</h1>
            <div className="text-[10px] uppercase tracking-[0.32em] text-amber-300/70">Portfolio · Plays · IRA — TV auto-refreshes</div>
          </div>
          <Link to="/tv" className="ml-auto rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs uppercase tracking-widest text-slate-400 hover:text-white">Open TV →</Link>
        </div>

        <div className="mb-4 flex items-center gap-2">
          <TabBtn active={tab === "stocks"} onClick={() => setTab("stocks")} testid="admin-tab-stocks"><TrendingUp className="inline h-4 w-4 mr-1" />Stocks ({holdings.stocks.length})</TabBtn>
          <TabBtn active={tab === "plays"} onClick={() => setTab("plays")} testid="admin-tab-plays"><Target className="inline h-4 w-4 mr-1" />Plays ({holdings.plays.length})</TabBtn>
          <TabBtn active={tab === "ira"} onClick={() => setTab("ira")} testid="admin-tab-ira"><Shield className="inline h-4 w-4 mr-1" />IRA ({holdings.ira.length})</TabBtn>
        </div>

        {loading ? (
          <div className="text-slate-500">Loading holdings…</div>
        ) : (
          <>
            {tab === "stocks" && <StocksTab rows={holdings.stocks} reload={reload} />}
            {tab === "plays"  && <PlaysTab  rows={holdings.plays}  reload={reload} />}
            {tab === "ira"    && <IRATab    rows={holdings.ira}    reload={reload} />}
          </>
        )}
      </div>
    </div>
  );
}
